import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import clsx from 'clsx';
import { AnimatedItem } from '../../../components/animations/AnimatedItem';
import { Grid, Typography, TextField, Tooltip, IconButton, Popper, AccordionSummary, AccordionDetails, InputAdornment, Collapse, Fade } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker/DatePicker';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { SourceSelector } from '../../selectors/SourceSelector';
import { OfficeSelector } from '../../../MailingList/selectors/OfficeSelector';
import { UserFeature, UserFeatureEnum } from '../../../types/UserFeature';
import { ClientAndContactSelector } from '../../../components/ClientAndContactSelector';
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import { IndividualNameSummary, IndividualSelector } from '../../selectors/IndividualSelector';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Alert } from '@material-ui/lab';
import { EventsAndCampaignIAutoCompleteItem, EventsAndCampaignSelector } from '../../../notesSummary/selectors/EventsAndCampaignSelector';
import { TitleAutocompleteSelector } from '../../selectors/TitleAutocompleteSelector';
import InfoIcon from '@material-ui/icons/Info';
import { SpeechTextField } from '../../../components/SpeechTextField';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { DuplicateCheck } from '../../../hooks/useCrmDuplicateCheck';
import { EnquiryDefaultConfig } from '../../EnquirySummaryRepository';
import { TranslationTextList } from '../../../locales/common';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('ContactDetailsPanel');

type RenderDuplicateContactInfo = (
    form: FormApi<Enquiry>,
    values: AnyObject,
    duplicateCheck: DuplicateCheck | null,
) => React.ReactNode;

// Explicit request (EnquiryFormDrawer split, phase 1+2): this is the drawer's "Contact Details" accordion
// moved out of the 18k-line EnquiryFormDrawer.tsx. The JSX and every field's own name / validate /
// subscription / handler logic are unchanged - what changed is where it lives and how it gets its inputs.
//
//  - It no longer receives the whole `values` object from the top-level <Form>. The outer component
//    subscribes (FormSpy) and passes down only the handful of values that affect what is rendered, as
//    primitives, so the panel body re-renders when one of those changes - not on every keystroke.
//  - Event handlers read the form's values at the moment the event fires (the first line of each
//    handler), which is exactly what the render-time `values` closure held before.
//  - Every handler / validator prop is expected to be identity-stable (the drawer wraps them with
//    useStableCallback - they still call the latest version of the underlying function).
export interface ContactDetailsPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    translate: (key: keyof TranslationTextList) => string;
    formAction: string;
    enquiryDefaultConfig?: EnquiryDefaultConfig;

    isExpanded: boolean;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;

    duplicateCheck: DuplicateCheck | null;
    isEmailHasDuplicate: boolean;
    isUSClient: boolean;
    isActivityTab: boolean;
    isAnyContactMethodMandatory: boolean;
    isSourceTypeClientOnly: true | undefined;

    isDuplicatePopperOpen: boolean;
    setIsDuplicatePopperOpen: (isOpen: boolean) => void;
    duplicatePopperAnchorRef: React.RefObject<HTMLButtonElement>;
    renderDuplicateContactInfo: RenderDuplicateContactInfo;

    getCorrespondenceRequired: (values: AnyObject) => boolean;
    requiredValidator: FieldValidator<any>; // tslint:disable-line: no-any
    requiredDateValidator: FieldValidator<any>; // tslint:disable-line: no-any
    dateValidator: FieldValidator<any>; // tslint:disable-line: no-any
    validateEmailField: FieldValidator<any>; // tslint:disable-line: no-any
    validatePhoneField: FieldValidator<any>; // tslint:disable-line: no-any
    validateMobileField: FieldValidator<any>; // tslint:disable-line: no-any

    checkDuplicateEmail: (input: {
        form: FormApi<Enquiry>,
        values: AnyObject,
        email: string,
        name: string | null,
        isFromButton?: boolean
    }) => void;
    onContactDetailsChanged: (form: FormApi<any>, values: AnyObject) => void; // tslint:disable-line: no-any
    onSwitch: (form: FormApi<Enquiry>, values: AnyObject, name: string) => void;
    onDropDownSelect: (
        form: FormApi<Enquiry>,
        values: AnyObject,
        input: FieldInputProps<any, HTMLElement>, // tslint:disable-line: no-any
        selection: any, // tslint:disable-line: no-any
        name: string,
    ) => void;
    onGetPartyAddresses: (form: FormApi<Enquiry>, values: AnyObject, selection: IndividualNameSummary, name: string) => void;
    onSourceChanged: (form: FormApi<Enquiry>, values: AnyObject, selection: IAutoCompleteItem) => void;
    onReferrerChanged: (form: FormApi<Enquiry>, values: AnyObject, selection: IAutoCompleteItem) => void;
}

// The values the rendered output actually depends on, derived from the form by the outer component.
interface ContactDetailsPanelBodyProps extends ContactDetailsPanelProps {
    panelTitle: string;
    isInitialEnquiryDateIsGreaterThanToday: boolean;
    isCorrespondenceRequired: boolean;
    isNameLockedToContact: boolean;
    isNameSwitched: boolean;
    individualSelector: IndividualNameSummary | null | undefined;
    isShowReferrer: boolean;
    referrerGuid: string | null | undefined;
    referrerName: string | null | undefined;
}

// Explicit request: the duplicate-contacts popover used to be built on every render of the drawer. It
// is only ever shown inside the <Popper> (which renders nothing while closed), so building it here
// means it is only created - and only subscribes to the form's values - while the popper is open,
// and then re-renders on value changes exactly as it did before.
const DuplicateContactInfo: React.FC<{
    form: FormApi<Enquiry>;
    duplicateCheck: DuplicateCheck | null;
    renderDuplicateContactInfo: RenderDuplicateContactInfo;
}> = ({ form, duplicateCheck, renderDuplicateContactInfo }) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => <>{renderDuplicateContactInfo(form, values, duplicateCheck)}</>}
    </FormSpy>
);

const ContactDetailsPanelBody: React.FC<ContactDetailsPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, translate, formAction, enquiryDefaultConfig,
        isExpanded, onExpandChange,
        duplicateCheck, isEmailHasDuplicate, isUSClient, isActivityTab, isAnyContactMethodMandatory, isSourceTypeClientOnly,
        isDuplicatePopperOpen, setIsDuplicatePopperOpen, duplicatePopperAnchorRef, renderDuplicateContactInfo,
        requiredValidator, requiredDateValidator, dateValidator, validateEmailField, validatePhoneField, validateMobileField,
        checkDuplicateEmail, onContactDetailsChanged, onSwitch, onDropDownSelect, onGetPartyAddresses,
        onSourceChanged, onReferrerChanged,
        panelTitle, isInitialEnquiryDateIsGreaterThanToday, isCorrespondenceRequired, isNameLockedToContact,
        isNameSwitched, individualSelector, isShowReferrer, referrerGuid, referrerName,
    } = bodyProps;

    const isEmailExist = (duplicateCheck && duplicateCheck.partyDuplicateCount > 0)
        || (duplicateCheck && duplicateCheck.enquiryContactDuplicateCount > 0)
        || (duplicateCheck && duplicateCheck.otherSideEmailAddress);

    // Names the matches the last check found. When it changes while the notice is already showing (the name or email was edited and found something
    // different), the notice is remounted by this key so it fades in and flashes again, instead of staying as it was.
    const duplicateMatchesSignature = duplicateCheck
        ? [
            duplicateCheck.otherSideEmailAddress,
            ...(duplicateCheck.contactMatterList || []).map((matter) => matter.nameId),
            ...(duplicateCheck.contactMatterListByName || []).map((matter) => matter.nameId),
            ...(duplicateCheck.contactEnquiries || []).map((row) => row.enquiryGuid),
            ...(duplicateCheck.contactEnquiriesByName || []).map((row) => row.enquiryGuid),
            ...(duplicateCheck.contactEnquriesOtherSide || []).map((row) => row.enquiryGuid),
            ...(duplicateCheck.contactEnquriesOtherSideByName || []).map((row) => row.enquiryGuid),
        ].join('|')
        : '';
    const isDuplicateNoticeShown = (isEmailExist && formAction !== 'edit') || (isEmailHasDuplicate && formAction === 'edit');
    // The key only follows the matches while the notice is showing. When a check finds nothing the signature goes empty at the same moment the notice starts
    // to fade out, and changing the key then would remount it and replay the entrance (fade-up and flash) in the middle of the exit.
    const noticeKeyRef = React.useRef('');
    if (isDuplicateNoticeShown) {
        noticeKeyRef.current = duplicateMatchesSignature;
    }

    return (
        <Accordion
            className={classes.expansionPanel}
            // defaultExpanded={isCreateForm}
            expanded={isExpanded}
            onChange={onExpandChange}

        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="contact-details-content"
                id="contact-details-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipBlue)}>
                        <PersonOutlineIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelBlue, classes.sectionLabelText)}>
                        Contact Details
                    </Typography>
                </div>

                <div className={classes.infoContainer}>
                    <div className={`${classes.truncateText} label`}>
                        {panelTitle}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                {/* Explicit bug report: spacing={3} (24px) read as too tall combined with
                    outlined+small fields - spacing={2} (16px) matches
                    MarketingRuleSetupDrawerForm.tsx's own reference density (its
                    FormSectionCard field grids all use spacing={2}). */}
                <Grid container={true} spacing={2}>
                    {isInitialEnquiryDateIsGreaterThanToday && (
                        <Grid item={true} xs={12} sm={12} md={12} lg={12}>
                            <Alert severity="warning">
                                The {translate('Initial Enquiry')} date is in the future
                            </Alert>
                        </Grid>
                    )}
                    {/* When a later check finds no duplicate the notice fades and folds away (Collapse + Fade) instead of vanishing. The Grid item's spacing
                        padding is set here because the Collapse wrapper sits between it and the Grid container: none above (the panel's own top padding already
                        leaves room) and extra below, so the next field's floating label does not touch the notice. */}
                    <Collapse in={!!isDuplicateNoticeShown} timeout={250} unmountOnExit={true} style={{ width: '100%' }}>
                        <Fade in={!!isDuplicateNoticeShown} timeout={250}>
                        <Grid item={true} xs={12} sm={12} md={12} lg={12} style={{ display: 'flex', alignItems: 'center', padding: '0 8px 14px' }}>
                            <div className={classes.associatedWarning}>
                                {/* Explicit request: this used to just pop into place the instant a
                                    duplicate was found - same fade/rise-in as the rest of this
                                    redesign. Only ever mounts fresh (the surrounding condition isn't
                                    met until a duplicate is actually found), so no key is needed for
                                    it to replay on a later, separate check. Popper deliberately kept
                                    OUTSIDE this, not nested inside it - it uses disablePortal, so it
                                    renders as a real DOM child of whatever wraps it; nested inside
                                    AnimatedItem it inherited that element's own opacity/transform
                                    animation, making the popover itself render transparent -
                                    explicit bug report. */}
                                <AnimatedItem key={noticeKeyRef.current} noFill={true} className={clsx(classes.associatedWarningInline, classes.duplicateNotice)}>
                                    <Typography className={classes.duplicateNoticeText}>A contact or enquiry with this name or email already exists. Hover over the icon to see the matches.</Typography>
                                    <IconButton
                                        ref={duplicatePopperAnchorRef}
                                        // onClick={() => setOpen((o) => !o)}
                                        onMouseEnter={() => setIsDuplicatePopperOpen(true)}
                                        size="small"
                                    >
                                        <InfoIcon className={classes.duplicateNoticeIcon} />
                                    </IconButton>
                                </AnimatedItem>
                                <Popper
                                    open={isDuplicatePopperOpen}
                                    anchorEl={duplicatePopperAnchorRef.current}
                                    placement="bottom-start"
                                    className={classes.popperWrapper}
                                    disablePortal={true}
                                    // onMouseLeave={() => setOpen(false)} // <-- Add this to close Popper when mouse leaves
                                >
                                    <DuplicateContactInfo
                                            form={form}
                                            duplicateCheck={duplicateCheck}
                                            renderDuplicateContactInfo={renderDuplicateContactInfo}
                                        />
                                </Popper>
                                {/* <LightTooltip title={showAllContactInfo(duplicateCheck)}>
                                    <InfoIcon className={classes.associatedColor} />
                                </LightTooltip> */}
                            </div>
                        </Grid>
                        </Fade>
                    </Collapse>
                    {formAction === 'edit' && duplicateCheck && !isEmailHasDuplicate && (
                        <Grid item={true} xs={12} sm={12} md={12} lg={12} style={{ display: 'flex', alignItems: 'center' }}>
                            <div className={classes.associatedWarning}>
                                <Typography className={classes.associatedColor}>
                                    No Duplicate Email Found.
                                </Typography>
                            </div>
                        </Grid>
                    )}
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="title"
                            // validate={required} 
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <>
                                    <TitleAutocompleteSelector
                                        {...input}
                                        label="Title"
                                        variant="outlined"
                                        size="small"
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        value={input.value ? { label: input.value, value: input.value } : undefined}
                                        // tslint:disable-next-line: max-line-length
                                        onSelection={(selection: IAutoCompleteItem, name: string) => {
                                            input.onChange(selection ? selection.value : '');
                                        }}
                                        // required={true}
                                        helperText={meta.error && meta.touched ? 'Title is required' : ''}
                                    />
                                </>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="firstName"
                            validate={!isActivityTab ? requiredValidator : undefined}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    {/* tslint:disable-next-line */}
                                    {/* {console.log('meta ' + meta)} */}
                                    <TextField
                                        {...input}
                                        label="Given Name(s)"
                                        variant="outlined"
                                        size="small"
                                        type="text"
                                        required={!isActivityTab}
                                        disabled={isNameLockedToContact}
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        autoComplete="abcd"
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? 'Given Name(s) is required'
                                                : ''
                                        }
                                        onChange={(event) => {
                                            const values = form.getState().values;
                                            input.onChange(event.target.value);
                                            checkDuplicateEmail({
                                                form,
                                                values,
                                                email: values.email,
                                                name: event.target.value?.trim() && values.lastName?.trim() ? `${event.target.value.trim()} ${values.lastName.trim()}` : null,
                                            })
                                        }}
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div className={classes.toggleFieldWrapepr}>
                            <div className="field">
                                {
                                    isNameSwitched ? (
                                        <Field
                                            name="individualSelector"
                                            subscription={{ touched: true, error: true, value: true }}
                                        >
                                            {({ input, meta }) => (
                                                <IndividualSelector
                                                    {...input}
                                                    key="individualSelector"
                                                    label="Individual Name"
                                                    variant="outlined"
                                                    size="small"
                                                    value={individualSelector}
                                                    error={meta.error && meta.touched}
                                                    helperText={
                                                        meta.error &&
                                                            meta.touched
                                                            ? 'Individual Name is required'
                                                            : ''
                                                    }
                                                    onSelection={(selection: IndividualNameSummary, name: string) => {
                                                        const values = form.getState().values;
                                                        input.onChange(selection);
                                                        onDropDownSelect(form, values, input, selection, 'individualSelector');
                                                        onGetPartyAddresses(form, values, selection, 'individualSelector');
                                                    }}
                                                    enableSecondary={true}
                                                    includeOrganisation={false}
                                                    isSourceTypeClientOnly={isSourceTypeClientOnly}
                                                    // Explicit request: same embedded switch button the lastName
                                                    // TextField below gets, instead of the separate "switcher"
                                                    // column this used to share with it.
                                                    endAdornmentExtra={(
                                                        <Tooltip
                                                            title={
                                                                <Typography color="inherit">Switch to enter the last name</Typography>
                                                            }
                                                        >
                                                            <IconButton
                                                                aria-label="Switch to enter the last name"
                                                                className={classes.nameSwitchAdornmentButton}
                                                                onClick={() => {
                                                                    const values = form.getState().values;
                                                                    onSwitch(form, values, 'lastName');
                                                                }}
                                                            >
                                                                <SyncAltIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                // value={input.value}
                                                />
                                            )}
                                        </Field>
                                    ) :
                                        (
                                            <Field
                                                name="lastName"
                                                validate={!isActivityTab ? requiredValidator : undefined}
                                                subscription={{ touched: true, error: true, value: true }}
                                            >
                                                {({ input, meta }) => (
                                                    <TextField
                                                        {...input}
                                                        label="Last Name"
                                                        variant="outlined"
                                                        size="small"
                                                        type="text"
                                                        required={!isActivityTab}
                                                        className={classes.textField}
                                                        error={
                                                            meta.error &&
                                                            meta.touched
                                                        }
                                                        disabled={isNameLockedToContact}
                                                        autoComplete="abcd"
                                                        helperText={
                                                            meta.error &&
                                                                meta.touched
                                                                ? 'Last Name is required'
                                                                : ''
                                                        }
                                                        onChange={(event) => {
                                                            const values = form.getState().values;
                                                            input.onChange(event.target.value);
                                                            checkDuplicateEmail({
                                                                form,
                                                                values,
                                                                email: values.email,
                                                                name: values.firstName?.trim() && event.target.value?.trim() ? `${values.firstName.trim()} ${event.target.value.trim()}` : null,
                                                            })
                                                        }}
                                                        // Explicit request: the switch-to-existing-contact button moved
                                                        // inside this field as an end adornment instead of sitting in a
                                                        // separate sibling column - same onSwitch/tooltip as the
                                                        // IndividualSelector branch's own external switcher below (kept
                                                        // there since IndividualSelector doesn't accept an adornment to
                                                        // embed this into once switched).
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <Tooltip
                                                                        title={
                                                                            <Typography color="inherit">Switch to select an existing client & contact</Typography>
                                                                        }
                                                                    >
                                                                        <IconButton
                                                                            aria-label="Switch to select an existing client & contact"
                                                                            className={classes.nameSwitchAdornmentButton}
                                                                            onClick={() => {
                                                                                const values = form.getState().values;
                                                                                onSwitch(form, values, 'lastName');
                                                                            }}
                                                                        >
                                                                            <SyncAltIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        )
                                }
                            </div>
                        </div>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="initialEnquiryDate"
                            validate={!isActivityTab ? requiredDateValidator : dateValidator}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <KeyboardDatePicker
                                    {...input}
                                    className={classes.textField}
                                    id="initialEnquiryDate"
                                    name="dateOpened"
                                    label={translate('Initial Enquiry')}
                                    format={isUSClient ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}
                                    placeholder={isUSClient ? 'mm/dd/yyyy' : 'dd/mm/yyyy'}
                                    required={!isActivityTab}
                                    animateYearScrolling={true}
                                    // tslint:disable-next-line: max-line-length
                                    autoOk={true}
                                    allowKeyboardControl={true}
                                    variant={'inline'}
                                    inputVariant="outlined"
                                    size="small"
                                    error={meta.error && meta.touched}
                                    inputProps={{ autoComplete: 'off' }}
                                    helperText={meta.error}
                                    onChange={(date: MaterialUiPickersDate | null, value?: string | null) => input.onChange(date)}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="email"
                            type="email"
                            validate={validateEmailField}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <TextField
                                        {...input}
                                        label="Email"
                                        variant="outlined"
                                        size="small"
                                        type="email"
                                        required={!isActivityTab ? isAnyContactMethodMandatory ? enquiryDefaultConfig?.isEmailMandatory : isCorrespondenceRequired : undefined}
                                        className={classes.textField}
                                        // tslint:disable-next-line: no-any
                                        // onChange={(event: any) => {
                                        //     input.onChange(event.target.value);
                                        //     onContactDetailsChanged(form, values);
                                        //     onCheckAssociateContact(form, event.target.value);
                                        // }}
                                        onChange={(event) => {
                                            const values = form.getState().values;
                                            input.onChange(event.target.value);
                                            checkDuplicateEmail({
                                                form,
                                                values,
                                                email: event.target.value,
                                                name: values.firstName?.trim() && values.lastName?.trim() ? `${values.firstName.trim()} ${values.lastName.trim()}` : null,
                                            });
                                        }}
                                        error={
                                            meta.error &&
                                            meta.touched
                                        }
                                        autoComplete="abcd"
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? meta.error
                                                : ''
                                        }
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="phone"
                            validate={validatePhoneField}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    {...input}
                                    label="Telephone"
                                    variant="outlined"
                                    size="small"
                                    type="text"
                                    // tslint:disable-next-line: no-any
                                    onChange={(event: any) => {
                                        const values = form.getState().values;
                                        input.onChange(event.target.value);
                                        onContactDetailsChanged(form, values);
                                    }}
                                    // required={true}
                                    required={!isActivityTab ? isAnyContactMethodMandatory ? enquiryDefaultConfig?.isPhoneNumberMandatory : isCorrespondenceRequired : undefined}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? meta.error // 'Phone is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="mobile"
                            validate={validateMobileField}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    {...input}
                                    label="Mobile"
                                    variant="outlined"
                                    size="small"
                                    type="text"
                                    // tslint:disable-next-line: no-any
                                    onChange={(event: any) => {
                                        const values = form.getState().values;
                                        input.onChange(event.target.value);
                                        onContactDetailsChanged(form, values);
                                    }}
                                    // required={true}
                                    required={!isActivityTab ? isAnyContactMethodMandatory ? enquiryDefaultConfig?.isMobileNumberMandatory : isCorrespondenceRequired : undefined}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? meta.error // 'Mobile is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>

                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="office"
                            validate={enquiryDefaultConfig && enquiryDefaultConfig.isLocationFieldMandatory ? requiredValidator : undefined}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <OfficeSelector
                                        {...input}
                                        label="Location"
                                        name="office"
                                        variant="outlined"
                                        size="small"
                                        required={enquiryDefaultConfig && enquiryDefaultConfig.isLocationFieldMandatory
                                            ? enquiryDefaultConfig.isLocationFieldMandatory
                                            : false
                                        }
                                        value={input.value ? input.value : null}
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        onSelection={(selection: IAutoCompleteItem, name: string) => {

                                            input.onChange(selection)

                                        }}
                                        helperText={meta.error && meta.touched
                                            ? 'Location is required'
                                            : ''
                                        }
                                        onBlur={input.onBlur}
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="source"
                            validate={!isActivityTab ? requiredValidator : undefined}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <SourceSelector
                                    label="Source"
                                    name="source"
                                    variant="outlined"
                                    size="small"
                                    value={input.value ? input.value : null}
                                    required={!isActivityTab}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    onSelection={(selection: IAutoCompleteItem, name: string) => {
                                        const values = form.getState().values;
                                        input.onChange(selection);

                                        onSourceChanged(form, values, selection);
                                    }}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Source is required'
                                            : ''
                                    }
                                    onBlur={input.onBlur}
                                />
                            )}
                        </Field>
                    </Grid>

                    {!!isShowReferrer && (
                        <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                            <Field name="referrer">
                                {({ input, meta }) => (
                                    <ClientAndContactSelector
                                        label="Contact"
                                        name="referrer"
                                        variant="outlined"
                                        size="small"
                                        required={false}
                                        onSelection={(selection: IAutoCompleteItem, name: string) => {
                                            const values = form.getState().values;
                                            input.onChange(selection);

                                            onReferrerChanged(form, values, selection);
                                        }}
                                        value={referrerGuid
                                            ? { value: referrerGuid, label: referrerName }
                                            : null
                                        }
                                        includeNonPmsContacts={true}
                                        includePmsContacts={true}
                                    />
                                )}
                            </Field>
                        </Grid>
                    )}
                    <Grid item={true} xs={isShowReferrer ? 12 : 12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="sourceNotes"
                            subscription={{ touched: true, error: true, value: true }}
                            validate={enquiryDefaultConfig?.isSourceNotesMandatory ? requiredValidator : undefined}
                        >
                            {({ input, meta }) => (
                                <SpeechTextField
                                    input={input}
                                    label="Source Notes"
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    autoComplete='abcd'
                                    className={classes.textField}
                                    required={enquiryDefaultConfig?.isSourceNotesMandatory}
                                    error={meta.error && meta.touched}
                                    helperText={meta.error && meta.touched
                                        ? 'Source Notes is required'
                                        : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid
                        item={true}
                        xs={12}
                        className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                    >
                        <Field
                            name="internalNotes"
                            subscription={{ touched: true, error: true, value: true }}
                            validate={enquiryDefaultConfig?.isInternalNotesMandatory ? requiredValidator : undefined}
                        >
                            {({ input, meta }) => (
                                <SpeechTextField
                                    input={input}
                                    label="Internal Notes"
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    autoComplete="abcd"
                                    className={classes.textField}
                                    required={enquiryDefaultConfig?.isInternalNotesMandatory}
                                    error={meta.error && meta.touched}
                                    helperText={meta.error && meta.touched
                                        ? 'Internal Notes is required'
                                        : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    {UserFeature.HasAccess(UserFeatureEnum.hasEventsAndCampaignsSummary) ? (
                        <>
                            <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field 
                                    name="eventAndCampaignSelector"
                                    validate={enquiryDefaultConfig?.isEventAndCampaignMandatory ? requiredValidator : undefined}
                                >
                                    {({ input, meta }) => (
                                        <EventsAndCampaignSelector
                                            {...input}
                                            // name="eventAndCampaignSelector"
                                            label={translate('Event/Campaign')}
                                            variant="outlined"
                                            size="small"
                                            error={meta.error && meta.touched}
                                            helperText={
                                                meta.error &&
                                                    meta.touched
                                                    ? `${translate('Event/Campaign')} is required`
                                                    : ''
                                            }
                                            onSelection={(selection: EventsAndCampaignIAutoCompleteItem, name: string) => {
                                                input.onChange(selection);
                                            }}
                                            className={classes.textField}
                                            required={enquiryDefaultConfig?.isEventAndCampaignMandatory}
                                            onBlur={input.onBlur}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid
                                item={true}
                                xs={6}
                                className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                            >
                                <Field
                                    name="eventsAndCampaignNotes"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <TextField
                                            {...input}
                                            label="Event/Campaign Notes"
                                            variant="outlined"
                                            size="small"
                                            type="text"
                                            // multiline={true}
                                            className={classes.textField}
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </>
                    ) : null}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
ContactDetailsPanelBody.displayName = 'ContactDetailsPanelBody';

export const ContactDetailsPanel: React.FC<ContactDetailsPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => {
            const tempReferrer = values.referrer ? values.referrer.label : values.referrerName ? values.referrerName : null;
            const tempSource = values.source ? values.source.label : null;

            // add all text in array and convert it to string to put comma for separation
            const panelTitle = [tempSource, tempReferrer, values.sourceNotes].filter(titlePart => titlePart).join(', ');

            // if initialEnquiryDate is greater than today, show warning
            const isInitialEnquiryDateIsGreaterThanToday = values.initialEnquiryDate
                ? moment(values.initialEnquiryDate).isAfter(moment(), 'day')
                : false;

            return (
                <ContactDetailsPanelBody
                    {...panelProps}
                    panelTitle={panelTitle}
                    isInitialEnquiryDateIsGreaterThanToday={isInitialEnquiryDateIsGreaterThanToday}
                    isCorrespondenceRequired={panelProps.getCorrespondenceRequired(values)}
                    isNameLockedToContact={!!values.nameGuid}
                    isNameSwitched={!!values.isNameSwitched}
                    individualSelector={values.individualSelector}
                    isShowReferrer={!!values.isShowReferrer}
                    referrerGuid={values.referrerGuid}
                    referrerName={values.referrerName}
                />
            );
        }}
    </FormSpy>
), arePropsEqualIgnoringForm);
ContactDetailsPanel.displayName = 'ContactDetailsPanel';
